import { useState, useEffect, useCallback } from 'react';
import { getDisplayError } from '../utils/errorHandler';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
const token = () => localStorage.getItem('token');
const fmt = (n) =>
  `GHS ${Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', sky: '#38BDF8', text: '#E8EDF5', sub: '#C5CDD8',
};

const AGING_META = {
  current: { label: 'Current',   bg: 'rgba(0,201,177,0.12)',   color: '#00C9B1', border: 'rgba(0,201,177,0.3)'   },
  '1-30':  { label: '1–30 days', bg: 'rgba(240,180,41,0.12)',  color: '#F0B429', border: 'rgba(240,180,41,0.3)'  },
  '31-60': { label: '31–60 days',bg: 'rgba(255,107,107,0.1)',  color: '#FF8C6B', border: 'rgba(255,107,107,0.3)' },
  '60+':   { label: '60+ days',  bg: 'rgba(255,107,107,0.15)', color: '#FF6B6B', border: 'rgba(255,107,107,0.35)'},
  paid:    { label: 'Paid',      bg: 'rgba(56,189,248,0.1)',   color: '#38BDF8', border: 'rgba(56,189,248,0.3)'  },
};

const PAYMENT_META = {
  cash:          { label: 'Cash',     color: '#00C9B1', bg: 'rgba(0,201,177,0.12)',   border: 'rgba(0,201,177,0.3)'   },
  momo:          { label: 'MoMo',     color: '#F0B429', bg: 'rgba(240,180,41,0.1)',   border: 'rgba(240,180,41,0.3)'  },
  card:          { label: 'Card',     color: '#38BDF8', bg: 'rgba(56,189,248,0.1)',   border: 'rgba(56,189,248,0.3)'  },
  bank_transfer: { label: 'Bank',     color: '#A78BFA', bg: 'rgba(167,139,250,0.1)',  border: 'rgba(167,139,250,0.3)' },
  credit:        { label: 'Credit',   color: '#FF6B6B', bg: 'rgba(255,107,107,0.1)',  border: 'rgba(255,107,107,0.3)' },
};

const TABS = [
  { key: 'all',         label: 'All'          },
  { key: 'outstanding', label: 'Outstanding'  },
  { key: 'partial',     label: 'Partial'      },
  { key: 'overdue',     label: 'Overdue (60+)'},
  { key: 'paid',        label: 'Paid'         },
];

const EMPTY_DEBT_FORM = { customerId: '', amount: '', dueDate: '', followUpDate: '', notes: '' };
const PAYMENT_OPTIONS = [
  { id: 'cash', label: 'Cash' }, { id: 'momo', label: 'MoMo' },
  { id: 'card', label: 'Card' }, { id: 'bank_transfer', label: 'Bank Transfer' },
];

const STYLES = `
  .db-page { padding: 28px 32px; max-width: 1280px; margin: 0 auto; font-family: 'Cabinet Grotesk', sans-serif; min-height: 100vh; background: ${C.ink}; }
  .db-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; margin-bottom: 26px; flex-wrap: wrap; }
  .db-badge { display: inline-flex; align-items: center; gap: 8px; padding: 6px 10px; border-radius: 999px; background: rgba(56,189,248,0.1); color: ${C.sky}; font-size: 0.7rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 12px; border: 1px solid rgba(56,189,248,0.2); }
  .db-title { margin: 0; font-size: 1.65rem; font-weight: 800; color: ${C.text}; letter-spacing: -0.3px; }
  .db-sub { margin: 8px 0 0; color: ${C.muted}; font-size: 0.9rem; max-width: 620px; }
  .db-actions { display: flex; gap: 10px; flex-wrap: wrap; justify-content: flex-end; }
  .db-btn-outline { background: rgba(255,255,255,0.05); border: 1.5px solid ${C.border}; border-radius: 10px; padding: 10px 16px; font-size: 0.82rem; font-weight: 700; color: ${C.sub}; cursor: pointer; font-family: inherit; transition: all 150ms; }
  .db-btn-outline:hover { background: rgba(255,255,255,0.09); color: ${C.text}; }
  .db-btn-primary { background: ${C.gold}; border: none; border-radius: 10px; padding: 10px 18px; font-size: 0.82rem; font-weight: 800; color: ${C.ink}; cursor: pointer; font-family: inherit; transition: opacity 150ms; }
  .db-btn-primary:hover { opacity: 0.88; }
  .db-error { background: rgba(255,107,107,0.1); border: 1px solid rgba(255,107,107,0.3); border-radius: 12px; padding: 12px 16px; font-size: 0.82rem; color: ${C.coral}; margin-bottom: 18px; display: flex; justify-content: space-between; gap: 12px; }
  .db-kpi-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; margin-bottom: 24px; }
  .db-kpi { background: ${C.surface}; border-radius: 16px; padding: 18px 20px; border: 1px solid ${C.border}; }
  .db-kpi-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
  .db-kpi-label { font-size: 0.7rem; font-weight: 700; color: ${C.muted}; letter-spacing: 0.06em; text-transform: uppercase; }
  .db-kpi-val { font-size: 1.35rem; font-weight: 800; color: ${C.text}; font-family: 'JetBrains Mono', monospace; }
  .db-kpi-sub { font-size: 0.74rem; color: ${C.muted}; margin-top: 4px; }
  .db-table-card { background: ${C.surface}; border-radius: 16px; border: 1px solid ${C.border}; overflow: hidden; }
  .db-table-toolbar { display: flex; align-items: center; justify-content: space-between; padding: 0 20px; border-bottom: 1px solid ${C.border}; }
  .db-tab-btn { background: none; border: none; padding: 12px 14px; font-size: 0.8rem; font-weight: 700; cursor: pointer; border-bottom: 2px solid transparent; color: ${C.muted}; transition: all 150ms; font-family: inherit; }
  .db-tab-btn.active { color: ${C.gold}; border-bottom-color: ${C.gold}; }
  .db-tab-btn:hover:not(.active) { color: ${C.sub}; }
  .db-table { width: 100%; border-collapse: collapse; }
  .db-table thead th { padding: 12px 16px; font-size: 0.7rem; font-weight: 700; color: ${C.muted}; letter-spacing: 0.05em; text-transform: uppercase; text-align: left; white-space: nowrap; background: rgba(255,255,255,0.02); border-bottom: 1px solid ${C.border}; }
  .db-table tbody tr { border-top: 1px solid ${C.border}; transition: background 150ms; }
  .db-table tbody tr:hover { background: rgba(255,255,255,0.025); }
  .db-td { padding: 14px 16px; vertical-align: top; }
  .db-mini-btn { background: rgba(255,255,255,0.05); border: 1px solid ${C.border}; border-radius: 8px; padding: 5px 10px; cursor: pointer; font-size: 0.72rem; font-weight: 700; color: ${C.sub}; font-family: inherit; transition: all 140ms; }
  .db-mini-btn:hover { background: rgba(255,255,255,0.09); color: ${C.text}; }
  .db-mini-btn-pay { border-color: rgba(0,201,177,0.3); color: ${C.teal}; background: rgba(0,201,177,0.08); }
  .db-mini-btn-pay:hover { background: rgba(0,201,177,0.14); }
  .db-mini-btn-del { border-color: rgba(255,107,107,0.3); color: ${C.coral}; background: rgba(255,107,107,0.07); }
  .db-mini-btn-del:hover { background: rgba(255,107,107,0.14); }
  .db-empty { padding: 52px 20px; text-align: center; }
  .db-loading { padding: 44px; text-align: center; color: ${C.muted}; }
  .db-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.65); backdrop-filter: blur(4px); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px; }
  .db-modal { background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 18px; width: 100%; padding: 26px 28px; box-shadow: 0 24px 60px rgba(0,0,0,0.55); max-height: 85vh; overflow-y: auto; }
  .db-modal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 22px; }
  .db-modal-title { margin: 0; font-size: 1.1rem; font-weight: 800; color: ${C.text}; }
  .db-modal-close { background: none; border: none; cursor: pointer; color: ${C.muted}; font-size: 1.25rem; line-height: 1; }
  .db-modal-close:hover { color: ${C.text}; }
  .db-field { margin-bottom: 14px; }
  .db-label { display: block; font-size: 0.74rem; font-weight: 700; color: ${C.muted}; margin-bottom: 6px; }
  .db-input { width: 100%; padding: 10px 12px; background: rgba(255,255,255,0.04); border: 1.5px solid ${C.border}; border-radius: 10px; font-size: 0.88rem; color: ${C.text}; outline: none; box-sizing: border-box; font-family: inherit; transition: border-color 150ms; }
  .db-input:focus { border-color: ${C.teal}; }
  .db-input::placeholder { color: ${C.muted}; }
  .db-select { width: 100%; padding: 10px 12px; background: rgba(255,255,255,0.04); border: 1.5px solid ${C.border}; border-radius: 10px; font-size: 0.88rem; color: ${C.text}; font-family: inherit; outline: none; }
  .db-select:focus { border-color: ${C.teal}; }
  .db-textarea { width: 100%; padding: 10px 12px; background: rgba(255,255,255,0.04); border: 1.5px solid ${C.border}; border-radius: 10px; font-size: 0.88rem; color: ${C.text}; outline: none; box-sizing: border-box; resize: vertical; font-family: inherit; }
  .db-textarea::placeholder { color: ${C.muted}; }
  .db-modal-err { background: rgba(255,107,107,0.1); border: 1px solid rgba(255,107,107,0.3); border-radius: 10px; padding: 10px 14px; font-size: 0.82rem; color: ${C.coral}; margin-bottom: 16px; }
  .db-modal-footer { display: flex; gap: 10px; margin-top: 22px; }
  .db-btn-cancel { flex: 1; padding: 11px 0; border-radius: 10px; border: 1.5px solid ${C.border}; background: rgba(255,255,255,0.04); color: ${C.muted}; font-weight: 700; cursor: pointer; font-family: inherit; }
  .db-btn-cancel:hover { color: ${C.text}; }
  .db-btn-confirm { flex: 1; padding: 11px 0; border-radius: 10px; border: none; background: ${C.gold}; color: ${C.ink}; font-weight: 800; cursor: pointer; font-family: inherit; transition: opacity 150ms; }
  .db-btn-confirm:disabled { opacity: 0.65; cursor: not-allowed; }
  .db-btn-pay { background: ${C.teal}; color: ${C.ink}; }
  .db-summary-strip { background: rgba(255,255,255,0.03); border-radius: 12px; padding: 14px 16px; margin-bottom: 18px; border: 1px solid ${C.border}; }
  .db-detail-grid { display: grid; grid-template-columns: 1.15fr 0.85fr; gap: 18px; }
  .db-detail-panel { background: rgba(255,255,255,0.03); border-radius: 14px; border: 1px solid ${C.border}; padding: 16px; }
  .db-panel-heading { font-size: 0.76rem; font-weight: 800; color: ${C.muted}; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 12px; }
  .db-detail-row { display: flex; justify-content: space-between; font-size: 0.84rem; color: ${C.sub}; border-bottom: 1px solid ${C.border}; padding-bottom: 8px; margin-bottom: 8px; }
  .db-detail-row:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
  .db-payment-card { background: rgba(255,255,255,0.04); border: 1px solid ${C.border}; border-radius: 12px; padding: 12px; margin-bottom: 8px; }
  .db-payment-card:last-child { margin-bottom: 0; }
  @media (max-width: 768px) {
    .db-kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .db-detail-grid { grid-template-columns: 1fr; }
    .db-page { padding: 16px; }
  }
`;

function AgingBadge({ aging }) {
  const meta = AGING_META[aging] || AGING_META.current;
  return (
    <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 800, background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}>
      {meta.label}
    </span>
  );
}

function PaymentBadge({ method }) {
  const meta = PAYMENT_META[method] || PAYMENT_META.cash;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 800, background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}>
      {meta.label}
    </span>
  );
}

function KpiCard({ label, value, sub, icon }) {
  return (
    <div className="db-kpi">
      <div className="db-kpi-top">
        <span className="db-kpi-label">{label}</span>
        <span style={{ fontSize: 18 }}>{icon}</span>
      </div>
      <div className="db-kpi-val">{value}</div>
      {sub && <div className="db-kpi-sub">{sub}</div>}
    </div>
  );
}

function ModalShell({ title, onClose, children, maxWidth = 520 }) {
  return (
    <div className="db-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="db-modal" style={{ maxWidth }}>
        <div className="db-modal-header">
          <h2 className="db-modal-title">{title}</h2>
          <button className="db-modal-close" onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="db-field">
      <label className="db-label">{label}</label>
      {children}
    </div>
  );
}

function RecordDebtModal({ customers, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_DEBT_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form.customerId) return setError('Please select a customer');
    if (!form.amount || Number(form.amount) <= 0) return setError('Enter a valid amount');
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/debts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          customerId: Number(form.customerId),
          amount: Number(form.amount),
          dueDate: form.dueDate || undefined,
          followUpDate: form.followUpDate || undefined,
          notes: form.notes || undefined,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw { error: data.error };
      }
      onSaved(await res.json());
      onClose();
    } catch (e) {
      console.error('[Debts] save balance error:', e);
      setError(getDisplayError(e, 'Failed to record balance. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title="Record Customer Balance" onClose={onClose}>
      {error && <div className="db-modal-err">{error}</div>}
      <Field label="Customer *">
        <select className="db-select" value={form.customerId} onChange={(e) => set('customerId', e.target.value)}>
          <option value="">Select customer…</option>
          {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Amount (GHS) *">
        <input className="db-input" type="number" value={form.amount} onChange={(e) => set('amount', e.target.value)} placeholder="0.00" />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Due Date">
          <input className="db-input" type="date" value={form.dueDate} onChange={(e) => set('dueDate', e.target.value)} />
        </Field>
        <Field label="Follow-up Date">
          <input className="db-input" type="date" value={form.followUpDate} onChange={(e) => set('followUpDate', e.target.value)} />
        </Field>
      </div>
      <Field label="Notes">
        <textarea className="db-textarea" value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} placeholder="What was sold on credit or what should be followed up?" />
      </Field>
      <div className="db-modal-footer">
        <button className="db-btn-cancel" onClick={onClose}>Cancel</button>
        <button className="db-btn-confirm" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Record Balance'}
        </button>
      </div>
    </ModalShell>
  );
}

function RecordPaymentModal({ debt, onClose, onPaid }) {
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handlePay = async () => {
    if (!amount || Number(amount) <= 0) return setError('Enter a valid amount');
    if (Number(amount) > debt.balance) return setError(`Amount exceeds balance of ${fmt(debt.balance)}`);
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/debts/${debt.id}/pay`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ amount: Number(amount), paymentMethod, notes: notes || undefined }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw { error: data.error };
      }
      onPaid(await res.json());
      onClose();
    } catch (e) {
      console.error('[Debts] record payment error:', e);
      setError(getDisplayError(e, 'Failed to record payment. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title="Record Collection" onClose={onClose} maxWidth={460}>
      <div className="db-summary-strip">
        <div style={{ fontSize: '0.76rem', color: C.muted, marginBottom: 4 }}>{debt.customer?.name}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', color: C.sub }}>
          <span>Balance due</span>
          <strong style={{ color: C.coral, fontFamily: "'JetBrains Mono', monospace" }}>{fmt(debt.balance)}</strong>
        </div>
      </div>
      {error && <div className="db-modal-err">{error}</div>}
      <Field label="Amount (GHS)">
        <input className="db-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" min="0.01" step="0.01" />
      </Field>
      <Field label="Received via">
        <select className="db-select" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
          {PAYMENT_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="Note">
        <textarea className="db-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Optional collection note" />
      </Field>
      <button onClick={() => setAmount(String(debt.balance))} style={{ background: 'none', border: 'none', color: C.teal, fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', padding: 0, marginTop: -4 }}>
        Apply full balance ({fmt(debt.balance)})
      </button>
      <div className="db-modal-footer">
        <button className="db-btn-cancel" onClick={onClose}>Cancel</button>
        <button className="db-btn-confirm db-btn-pay" onClick={handlePay} disabled={saving}>
          {saving ? 'Saving…' : 'Record Payment'}
        </button>
      </div>
    </ModalShell>
  );
}

function DebtDetailModal({ debt, loading, onClose, onRefresh }) {
  if (!debt && !loading) return null;
  return (
    <ModalShell title={loading ? 'Loading Balance…' : `Balance — ${debt.customer?.name || 'Customer'}`} onClose={onClose} maxWidth={720}>
      {loading ? (
        <div style={{ padding: '32px 0', textAlign: 'center', color: C.muted }}>Loading payment history…</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, marginBottom: 22 }}>
            {[
              { label: 'Total',    value: fmt(debt.amount),     icon: '📋' },
              { label: 'Paid',     value: fmt(debt.amountPaid), icon: '✅' },
              { label: 'Balance',  value: fmt(debt.balance),    icon: '💰' },
              { label: 'Payments', value: String(debt.paymentCount || 0), icon: '🧾' },
            ].map(({ label, value, icon }) => (
              <div key={label} className="db-kpi">
                <div className="db-kpi-top"><span className="db-kpi-label">{label}</span><span>{icon}</span></div>
                <div className="db-kpi-val" style={{ fontSize: '1.1rem' }}>{value}</div>
              </div>
            ))}
          </div>

          <div className="db-detail-grid">
            <div className="db-detail-panel">
              <div className="db-panel-heading">Balance Details</div>
              <div className="db-detail-row"><span>Status</span><AgingBadge aging={debt.aging} /></div>
              <div className="db-detail-row"><span>Created</span><strong style={{ color: C.text }}>{new Date(debt.createdAt).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' })}</strong></div>
              <div className="db-detail-row"><span>Due date</span><strong style={{ color: C.text }}>{debt.dueDate ? new Date(debt.dueDate).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not set'}</strong></div>
              <div className="db-detail-row"><span>Follow-up</span><strong style={{ color: C.text }}>{debt.followUpDate ? new Date(debt.followUpDate).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not set'}</strong></div>
              <div className="db-detail-row"><span>Last reminder</span><strong style={{ color: C.text }}>{debt.lastRemindedAt ? new Date(debt.lastRemindedAt).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Never sent'}</strong></div>
              {debt.sale && <div className="db-detail-row"><span>Linked sale</span><strong style={{ color: C.gold }}>Sale #{debt.sale.id}</strong></div>}
              {debt.sale && <div className="db-detail-row"><span>Collected at checkout</span><strong style={{ color: C.teal, fontFamily: "'JetBrains Mono', monospace" }}>{fmt(debt.sale.amountCollected || 0)}</strong></div>}
              {debt.notes && (
                <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
                  <div className="db-panel-heading">Notes</div>
                  <div style={{ fontSize: '0.83rem', color: C.sub, lineHeight: 1.6 }}>{debt.notes}</div>
                </div>
              )}
            </div>

            <div className="db-detail-panel" style={{ borderColor: 'rgba(240,180,41,0.2)', background: 'rgba(240,180,41,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div className="db-panel-heading" style={{ marginBottom: 0, color: C.gold }}>Payment History</div>
                <button onClick={onRefresh} style={{ border: 'none', background: 'none', color: C.gold, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Refresh</button>
              </div>
              {debt.payments?.length ? (
                <div>
                  {debt.payments.map((p) => (
                    <div key={p.id} className="db-payment-card">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <div style={{ fontWeight: 800, color: C.text, fontSize: '0.85rem', fontFamily: "'JetBrains Mono', monospace" }}>{fmt(p.amount)}</div>
                        <PaymentBadge method={p.paymentMethod} />
                      </div>
                      <div style={{ fontSize: '0.73rem', color: C.muted }}>{new Date(p.createdAt).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                      {p.notes && <div style={{ fontSize: '0.76rem', color: C.sub, marginTop: 6 }}>{p.notes}</div>}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: '30px 12px', textAlign: 'center', color: C.muted, fontSize: '0.82rem' }}>No payments recorded yet.</div>
              )}
            </div>
          </div>
        </>
      )}
    </ModalShell>
  );
}

export default function Debts() {
  const [debts, setDebts]               = useState([]);
  const [customers, setCustomers]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [activeTab, setActiveTab]       = useState('all');
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [payingDebt, setPayingDebt]     = useState(null);
  const [detailDebt, setDetailDebt]     = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [remindingId, setRemindingId]   = useState(null);
  const [error, setError]               = useState('');

  const loadDebts = useCallback(async () => {
    const res = await fetch(`${API}/debts`, { headers: { Authorization: `Bearer ${token()}` } });
    if (!res.ok) throw new Error('Could not load balances');
    const data = await res.json();
    setDebts(data);
    return data;
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        const [debtsRes, customersRes] = await Promise.all([
          fetch(`${API}/debts`, { headers: { Authorization: `Bearer ${token()}` } }),
          fetch(`${API}/customers`, { headers: { Authorization: `Bearer ${token()}` } }),
        ]);
        setDebts(await debtsRes.json());
        setCustomers(await customersRes.json());
      } catch (e) {
        console.error('[Debts] load balances error:', e);
        setError(getDisplayError(e, 'Failed to load balances. Please try again.'));
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  const openDebtDetail = async (debtId) => {
    setDetailLoading(true);
    setDetailDebt(null);
    try {
      const res = await fetch(`${API}/debts/${debtId}`, { headers: { Authorization: `Bearer ${token()}` } });
      const data = await res.json();
      if (!res.ok) throw { error: data.error };
      setDetailDebt(data);
    } catch (e) {
      console.error('[Debts] load balance details error:', e);
      setError(getDisplayError(e, 'Failed to load balance details. Please try again.'));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this customer balance?')) return;
    await fetch(`${API}/debts/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } });
    setDebts((prev) => prev.filter((debt) => debt.id !== id));
    if (detailDebt?.id === id) setDetailDebt(null);
  };

  const handleRemind = async (debt) => {
    if (!debt.customer?.email) { setError(`${debt.customer?.name} has no email address on file.`); return; }
    setRemindingId(debt.id);
    try {
      const res = await fetch(`${API}/debts/remind/${debt.id}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      if (!res.ok) throw { error: data.error };
      const remindedAt = new Date().toISOString();
      setDebts((prev) => prev.map((item) => (item.id === debt.id ? { ...item, lastRemindedAt: remindedAt } : item)));
      setDetailDebt((prev) => (prev?.id === debt.id ? { ...prev, lastRemindedAt: remindedAt } : prev));
    } catch (e) {
      console.error('[Debts] send reminder error:', e);
      setError(getDisplayError(e, 'Failed to send reminder. Please try again.'));
    } finally {
      setRemindingId(null);
    }
  };

  const handleSendAllReminders = async () => {
    const outstanding = debts.filter((debt) => debt.status !== 'paid' && debt.customer?.email);
    for (const debt of outstanding) {
      await fetch(`${API}/debts/remind/${debt.id}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token()}` },
      }).catch(() => {});
    }
    loadDebts().catch(() => {});
  };

  const outstanding    = debts.filter((debt) => debt.status !== 'paid');
  const totalOutstanding = outstanding.reduce((sum, debt) => sum + debt.balance, 0);
  const overdue        = debts.filter((debt) => debt.aging === '60+');
  const totalOverdue   = overdue.reduce((sum, debt) => sum + debt.balance, 0);
  const now            = new Date();
  const weekEnd        = new Date(now.getTime() + 7 * 86400000);
  const dueThisWeek    = outstanding.filter((debt) => debt.dueDate && new Date(debt.dueDate) >= now && new Date(debt.dueDate) <= weekEnd);
  const avgDebt        = outstanding.length ? totalOutstanding / outstanding.length : 0;

  const filtered = debts.filter((debt) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'overdue') return debt.aging === '60+';
    return debt.status === activeTab;
  });

  return (
    <div className="db-page">
      <style>{STYLES}</style>

      <div className="db-header">
        <div>
          <div className="db-badge">Retail &amp; Wholesale Collections</div>
          <h1 className="db-title">Customer Balances</h1>
          <p className="db-sub">Track pay-later sales, follow overdue buyers, and record collections in one place.</p>
        </div>
        <div className="db-actions">
          <button className="db-btn-outline" onClick={handleSendAllReminders}>Send reminders</button>
          <button className="db-btn-primary" onClick={() => setShowRecordModal(true)}>Record balance</button>
        </div>
      </div>

      {error && (
        <div className="db-error">
          <span>{error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', color: C.coral, cursor: 'pointer', fontWeight: 800, fontSize: '1rem' }}>✕</button>
        </div>
      )}

      <div className="db-kpi-grid">
        <KpiCard label="Outstanding"    value={fmt(totalOutstanding)} sub={`${outstanding.length} active balances`}  icon="💳" />
        <KpiCard label="60+ Overdue"    value={fmt(totalOverdue)}     sub={`${overdue.length} buyers overdue`}        icon="⏰" />
        <KpiCard label="Due This Week"  value={fmt(dueThisWeek.reduce((s, d) => s + d.balance, 0))} sub={`${dueThisWeek.length} due soon`} icon="📅" />
        <KpiCard label="Average Balance" value={fmt(avgDebt)}          sub="Per active customer"                      icon="📈" />
      </div>

      <div className="db-table-card">
        <div className="db-table-toolbar">
          <div style={{ display: 'flex' }}>
            {TABS.map((tab) => (
              <button key={tab.key} className={`db-tab-btn${activeTab === tab.key ? ' active' : ''}`} onClick={() => setActiveTab(tab.key)}>
                {tab.label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: '0.74rem', color: C.muted }}>Credit sales from POS land here automatically</div>
        </div>

        {loading ? (
          <div className="db-loading">Loading balances…</div>
        ) : !filtered.length ? (
          <div className="db-empty">
            <div style={{ fontSize: '2rem', marginBottom: 10 }}>💳</div>
            <div style={{ fontSize: '0.96rem', fontWeight: 700, color: C.sub, marginBottom: 6 }}>No balances here yet</div>
            <div style={{ fontSize: '0.82rem', color: C.muted }}>Use credit checkout in POS or record a manual balance for a customer.</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="db-table">
              <thead>
                <tr>
                  {['Customer', 'Source', 'Total', 'Balance', 'Due', 'Last Payment', 'Aging', 'Actions'].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((debt) => (
                  <tr key={debt.id}>
                    <td className="db-td">
                      <div style={{ fontWeight: 700, color: C.text, fontSize: '0.86rem' }}>{debt.customer?.name || '—'}</div>
                      <div style={{ fontSize: '0.73rem', color: C.muted, marginTop: 3 }}>{debt.customer?.phone || debt.customer?.email || 'No contact stored'}</div>
                      {debt.notes && <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: 5, maxWidth: 220 }}>{debt.notes}</div>}
                    </td>
                    <td className="db-td">
                      {debt.sale ? (
                        <>
                          <div style={{ fontWeight: 700, color: C.gold, fontSize: '0.82rem' }}>Sale #{debt.sale.id}</div>
                          <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: 4 }}>{new Date(debt.sale.createdAt).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                          {Number(debt.sale.amountCollected || 0) > 0 && (
                            <div style={{ fontSize: '0.72rem', color: C.teal, marginTop: 4, fontWeight: 700 }}>Paid now {fmt(debt.sale.amountCollected)}</div>
                          )}
                          <div style={{ marginTop: 6 }}><PaymentBadge method={debt.sale.paymentMethod} /></div>
                        </>
                      ) : (
                        <div style={{ fontSize: '0.78rem', color: C.muted }}>Manual record</div>
                      )}
                    </td>
                    <td className="db-td" style={{ fontSize: '0.84rem', fontWeight: 700, color: C.sub, fontFamily: "'JetBrains Mono', monospace" }}>{fmt(debt.amount)}</td>
                    <td className="db-td" style={{ fontSize: '0.86rem', fontWeight: 800, color: debt.balance > 0 ? C.coral : C.teal, fontFamily: "'JetBrains Mono', monospace" }}>{fmt(debt.balance)}</td>
                    <td className="db-td" style={{ fontSize: '0.79rem', color: C.muted }}>{debt.dueDate ? new Date(debt.dueDate).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Open'}</td>
                    <td className="db-td">
                      {debt.lastPayment ? (
                        <>
                          <div style={{ fontWeight: 700, color: C.text, fontSize: '0.8rem', fontFamily: "'JetBrains Mono', monospace" }}>{fmt(debt.lastPayment.amount)}</div>
                          <div style={{ margin: '4px 0' }}><PaymentBadge method={debt.lastPayment.paymentMethod} /></div>
                          <div style={{ fontSize: '0.72rem', color: C.muted }}>{new Date(debt.lastPayment.createdAt).toLocaleDateString('en-GH', { day: 'numeric', month: 'short' })}</div>
                        </>
                      ) : (
                        <span style={{ fontSize: '0.76rem', color: C.muted }}>No payment yet</span>
                      )}
                    </td>
                    <td className="db-td">
                      <div style={{ display: 'grid', gap: 6 }}>
                        <AgingBadge aging={debt.aging} />
                        <span style={{ fontSize: '0.72rem', color: C.muted }}>{debt.paymentCount || 0} payment{(debt.paymentCount || 0) === 1 ? '' : 's'}</span>
                      </div>
                    </td>
                    <td className="db-td">
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        <button className="db-mini-btn" onClick={() => openDebtDetail(debt.id)}>View</button>
                        {debt.status !== 'paid' && <button className="db-mini-btn db-mini-btn-pay" onClick={() => setPayingDebt(debt)}>Pay</button>}
                        <button className="db-mini-btn" disabled={remindingId === debt.id} onClick={() => handleRemind(debt)}>{remindingId === debt.id ? '…' : '📧'}</button>
                        <button className="db-mini-btn db-mini-btn-del" onClick={() => handleDelete(debt.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showRecordModal && (
        <RecordDebtModal customers={customers} onClose={() => setShowRecordModal(false)} onSaved={(debt) => setDebts((prev) => [debt, ...prev])} />
      )}

      {payingDebt && (
        <RecordPaymentModal
          debt={payingDebt}
          onClose={() => setPayingDebt(null)}
          onPaid={(updated) => {
            setDebts((prev) => prev.map((debt) => (debt.id === updated.id ? updated : debt)));
            setDetailDebt((prev) => (prev?.id === updated.id ? updated : prev));
          }}
        />
      )}

      {(detailLoading || detailDebt) && (
        <DebtDetailModal
          debt={detailDebt}
          loading={detailLoading}
          onClose={() => { setDetailDebt(null); setDetailLoading(false); }}
          onRefresh={() => detailDebt && openDebtDetail(detailDebt.id)}
        />
      )}
    </div>
  );
}
